import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

export default function AddBannedWordDialog({ open, onClose, onAdd, selectedWord = "", loading = false, language = "english" }) {
    const [newWord, setNewWord] = useState(selectedWord);
    const [newCategory, setNewCategory] = useState("other");
    const [newLanguage, setNewLanguage] = useState(language);

    // Categories definition
    const categories = [
        "profanity",
        "violence",
        "drugs",
        "offensive",
        "inappropriate",
        "high_frequency",
        "other"
    ];

    // Format category name for display (e.g., "high_frequency" -> "High Frequency")
    const formatCategoryLabel = (category) => {
        return category
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Update the word field when props change
    useEffect(() => {
        setNewWord(selectedWord);
    }, [selectedWord]);

    // Update language when prop language changes or dialog opens
    useEffect(() => {
        if (open) {
            setNewLanguage(language);
        }
    }, [open, language]);

    const handleAdd = () => {
        if (newWord.trim()) {
            onAdd(newWord.trim(), newCategory, newLanguage);
            setNewWord("");
            setNewCategory("other");
            setNewLanguage(language);
        }
    };

    const handleClose = () => {
        setNewWord("");
        setNewCategory("other");
        setNewLanguage(language);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add New Banned Word</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    {/* Language Selection */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                            Language
                        </Typography>
                        <ToggleButtonGroup
                            value={newLanguage}
                            exclusive
                            onChange={(_event, newLang) => {
                                if (newLang !== null) {
                                    setNewLanguage(newLang);
                                }
                            }}
                            size="small"
                            fullWidth
                        >
                            <ToggleButton value="english">
                                <Typography variant="body2" sx={{ px: 1 }}>
                                    English
                                </Typography>
                            </ToggleButton>
                            <ToggleButton value="french">
                                <Typography variant="body2" sx={{ px: 1 }}>
                                    French
                                </Typography>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Category Selection - Moved above word field */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                            Category
                        </Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                row
                            >
                                {categories.map(category => (
                                    <FormControlLabel
                                        key={category}
                                        value={category}
                                        control={<Radio size="small" />}
                                        label={formatCategoryLabel(category)}
                                        sx={{ mr: 2 }}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Word Field - Moved below category selection */}
                    <TextField
                        label="Word"
                        value={newWord}
                        onChange={(e) => setNewWord(e.target.value)}
                        fullWidth
                        placeholder="Enter the word to ban"
                        helperText={newLanguage === "french" ? "Accents normalized: café → cafe, été → ete" : ""}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleAdd} variant="contained" color="primary" disabled={loading}>
                    {loading ? "Adding..." : "Add Word"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
